/**
 * Подписи для входов ученика: сервер отдаёт ключи, слова рисует фронт
 * (договор `docs/CONTRACTS/devices.md` §3).
 *
 * ⚠️ НЕЗНАКОМОЕ ЗНАЧЕНИЕ ПОКАЗЫВАЕМ КАК ЕСТЬ И НЕ ПАДАЕМ. Договор прямо
 * предупреждает, что набор значений расширится: разбор user-agent на сервере
 * будут дополнять, и новый браузер не должен ни ронять экран, ни исчезать из
 * строки. Лучше сырое слово «vivaldi», чем пустое место.
 */

/**
 * ⚠️ ДАТА НАЧАЛА УЧЁТА. Строки копятся с выката миграции `000023`: на проде это
 * 12.09.2026 01:57 Астаны, на стенде 01:37. Старые входы задним числом не
 * восстанавливались (так поставлена задача), поэтому пустой список значит «с
 * этого дня не входил», а НЕ «никогда не входил». Разница существенная:
 * сотрудник, прочитавший «никогда», решит, что ученик не занимается вовсе.
 *
 * Разница между стендом и продом — двадцать минут, в пределах суток это одна и
 * та же дата, и день на экране один.
 */
export const DEVICE_LOG_SINCE = '2026-09-12';

/** Ключи, у которых подпись есть в локалях. */
const DEVICE_TYPES = ['phone', 'tablet', 'computer', 'other'] as const;
const OS_KEYS = ['ios', 'android', 'windows', 'macos', 'linux', 'chromeos'] as const;
const BROWSER_KEYS = ['safari', 'chrome', 'edge', 'yandex', 'firefox', 'opera', 'samsung', 'webview'] as const;

export const KNOWN_DEVICE_TYPES: readonly string[] = DEVICE_TYPES;
export const KNOWN_OS: readonly string[] = OS_KEYS;
export const KNOWN_BROWSERS: readonly string[] = BROWSER_KEYS;

/** Ключ локали для типа устройства; незнакомый — `null`, показываем сырое значение. */
export function deviceTypeKey(value: string): string | null {
  return (DEVICE_TYPES as readonly string[]).includes(value) ? `studentDevices.type.${value}` : null;
}

/**
 * Ключ локали для системы. `other` — `null` И ПУСТОЕ ЗНАЧЕНИЕ: договор велит его
 * не показывать вовсе. «Неизвестная система» рядом с «Неизвестное устройство»
 * читалось бы как поломка, хотя это просто редкий user-agent.
 */
export function osKey(value: string): string | null {
  if (!value || value === 'other') return null;
  return (OS_KEYS as readonly string[]).includes(value) ? `studentDevices.os.${value}` : null;
}

/** Ключ локали для браузера; `other` не показывается, как и у системы. */
export function browserKey(value: string): string | null {
  if (!value || value === 'other') return null;
  return (BROWSER_KEYS as readonly string[]).includes(value) ? `studentDevices.browser.${value}` : null;
}

/** Показывать ли значение вовсе: `other` и пустое у системы и браузера — нет. */
export function isHiddenValue(value: string): boolean {
  return !value || value === 'other';
}

/**
 * Время входа: дата с ГОДОМ и часы с минутами.
 *
 * ⚠️ ГОД ОБЯЗАТЕЛЕН, И ЭТО НЕ ПРИДИРКА. Общий `formatDateTime` из
 * `lib/aiPipeline` печатает «12.09 18:40» без года — он писался для очереди
 * заданий, где всё случилось сегодня. Здесь срок хранения строки — ГОД
 * (решение владельца 12.09.2026), и вход годовой давности выглядел бы как
 * сегодняшний: сотрудник решил бы, что ученик заходил только что.
 *
 * Секунды не показываем: в вопросе «когда последний раз заходил» они шум.
 * Кривое или пустое значение — пустая строка, а не «Invalid Date».
 */
export function formatLoginAt(value: string | null | undefined, lang: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const locale = lang.startsWith('kk') ? 'kk-KZ' : lang.startsWith('en') ? 'en-GB' : 'ru-RU';
  return d.toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
